import { Component, OnInit } from '@angular/core';
import { VTableRowStatus } from '@app/directives/_layout/v-table/_enums/VTableRowStatus.enum';
import { IVTableColumn } from '@app/directives/_layout/v-table/_models/IVTableColumn';
import { IVTableRow } from '@app/directives/_layout/v-table/_models/IVTableRow';
import { VTableService } from '@app/directives/_layout/v-table/_services/v-table.service';
import { IVerticalFilterListItem } from '@app/directives/_resourceFilters/filter-bar/filter-bar.models';
import { SortDirection } from '@app/enum/SortDirection';
import { SortType } from '@app/enum/SortType';
import { IReseller } from '@app/main/modules/foundation/_models/IReseller';
import { AccountService } from '@app/main/modules/foundation/_services/account.service';
import { FoundationNavigationModule } from '@app/main/modules/foundation/foundation.navigation';
import { IAccount } from '@app/services/account.service';
import { DateUnit } from '@app/services/date.service';
import { NavigationService } from '@app/services/navigation.service';
import { TranslationService } from '@app/services/translation.service';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { from, switchMap, take } from 'rxjs';
import { DeviceHealthService } from '../../device-health.service';
import { DeviceHealthRecordType } from '../../enums/device-health-record-type';
import { DeviceHealthRecentVehicleSummary } from '../../models/device-health-recent-vehicle-summary';
import { DeviceHealthVehicleDurationSummary } from '../../models/device-health-vehicle-duration-summary';
import './device-health-vehicles.component.scss';

@UntilDestroy()
@Component({
    selector: 'device-health-vehicles',
    template: require('./device-health-vehicles.component.html')
})
export class DeviceHealthVehiclesComponent implements OnInit {
    loading = false;

    tableColumns: IVTableColumn<DeviceHealthVehicleDurationSummary>[] = [];
    tableRows: IVTableRow<DeviceHealthVehicleDurationSummary>[] = [];
    data: DeviceHealthVehicleDurationSummary[] = [];
    cameraRecordTypeFilterItems: IVerticalFilterListItem<DeviceHealthVehicleDurationSummary>[] = [];
    telematicsRecordTypeFilterItems: IVerticalFilterListItem<DeviceHealthVehicleDurationSummary>[] = [];
    resellerFilterItems: IVerticalFilterListItem<DeviceHealthVehicleDurationSummary>[] = [];
    selectedRecordTypes: DeviceHealthRecordType[] = [];

    accounts: IAccount[] = [];
    resellers: IReseller[] = [];

    selectedAccount?: IAccount;

    worstTenPercentMap = new Map<number, DeviceHealthVehicleDurationSummary>();

    exportTitle = this.translationService.t('DeviceHealth.Vehicles');

    constructor(
        private readonly navigationService: NavigationService,
        private readonly deviceHealthService: DeviceHealthService,
        private readonly vTableService: VTableService,
        private readonly accountService: AccountService,
        private readonly translationService: TranslationService,
    ) {}

    ngOnInit(): void {
        this.createTable();
        this.loadData();
        this.getAccounts();
    }

    selectSummary(row: IVTableRow<DeviceHealthRecentVehicleSummary>): void {
        const data: { [key: string]: string | number } = { 
            vehicleId: row.data.vehicleId,
        }

        if (this.selectedRecordTypes.length > 0) {
            data.filterBy = this.selectedRecordTypes.join(',');
        }

        this.navigationService.go(FoundationNavigationModule.DeviceHealthVehicleView, data); 
    }

    onRecordTypeChange(items: IVerticalFilterListItem[]): void {
        this.selectedRecordTypes = items.map(item => item.key);
    }

    filterSummaryByAccount(summary: DeviceHealthRecentVehicleSummary, account: IAccount): boolean {
        return summary.accountId === account.Id;
    }

    accountSearchPredicate(account: IAccount, searchText: string): boolean {
        return account.CompanyName.toLowerCase().indexOf(searchText) >= 0;
    }

    selectAccount(account: IAccount): void {
        this.selectedAccount = this.accounts.find(x => x.Id === account.Id);
    }

    private createTable(): void {
        this.tableColumns = [
            {
                header: this.vTableService.createStatusTableHeader(),
                createCellFunction: summary => {
                    let status = VTableRowStatus.Info;

                    if (this.worstTenPercentMap.get(summary.vehicleId)) {
                        status = VTableRowStatus.Error;
                    }

                    return this.vTableService.createStatusCell(status, {sortData: summary.score});
                }
            },
            {
                header: this.vTableService.createVehicleTableHeader(),
                createCellFunction: summary => this.vTableService.createVehicleRegCell({ Id: summary.vehicleId, Reg: summary.reg })
            },
            {
                header: this.vTableService.createTableHeader({ tKey: "Common.Account", sortable: false }),
                createCellFunction: (summary) => this.vTableService.createTableCell(summary.companyName, { tooltip: true })
            },
            {
                header: this.vTableService.createTableHeader({ tKey: "DeviceHealth.RecordsCount", sortable: true, sortType: SortType.Number, sortDirection: SortDirection.Descending, width: 160 }),
                createCellFunction: summary => this.vTableService.createTableCell(summary.recordsCount)
            },
            {
                header: this.vTableService.createTableHeader({ tKey: "DeviceHealth.CameraRecordTypes" }),
                createCellFunction: (summary) => this.vTableService.createTableCell(summary.cameraRecordTypesDesc, { tooltip: true })
            },
            {
                header: this.vTableService.createTableHeader({ tKey: "DeviceHealth.TelematicsRecordTypes" }),
                createCellFunction: (summary) => this.vTableService.createTableCell(summary.telematicsRecordTypesDesc, { tooltip: true })
            },
            {
                header: this.vTableService.createTableHeader({ tKey: "DeviceHealth.TotalDuration", sortType: SortType.Number, width: 160}),
                createCellFunction: (summary) => this.vTableService.createDurationCell(summary.totalDuration, DateUnit.Sec)
            },
        ]
    }

    
    private loadData(): void {
        this.loading = true;

        this.deviceHealthService.getSummariesWithCompany()
            .pipe(
                untilDestroyed(this),
                take(1),
                switchMap(data => {
                    this.data = data.sort((a, b) => a.score - b.score);
                    this.getWorstTenPercent();
                    this.createTableRows();
                    this.createRecordTypeFilterItems();
                    
                    return from(this.createResellerFilterItems());
                })
        ).subscribe(() => {
            this.loading = false;
        });
    }

    private getWorstTenPercent() {
        const worstTenPercent = [...this.data.slice(this.data.length * 0.9)];

        this.worstTenPercentMap = new Map(worstTenPercent.map(veh => [veh.vehicleId, veh]));
    }

    private createTableRows(): void {
        this.tableRows = this.vTableService.createTableRows(this.data, this.tableColumns);
    }

    private createRecordTypeFilterItems(): void {
        this.cameraRecordTypeFilterItems = this.deviceHealthService.getRecordTypeFiltersForVehicleSummaries(this.data, 'camera');
        this.telematicsRecordTypeFilterItems = this.deviceHealthService.getRecordTypeFiltersForVehicleSummaries(this.data, 'telematics');
    }

    private async createResellerFilterItems(): Promise<void> {
        this.resellerFilterItems = await this.deviceHealthService.getResellerFiltersForVehicleSummaries(this.data);
    }

    private getAccounts(): void {
        this.accountService.getCustomerAccounts().pipe(untilDestroyed(this))
            .subscribe(accounts => {
                this.accounts = accounts;
            });
    }
}
